"use client";

import { useId } from "react";

/*
 * Hornaut-Figur (Marke, Design-Guide `hornaut-figur.md`, Musterkatalog M-018, Betreiber 09.10.2026 „A, Stufe 1 bauen“).
 * Ganzkörper-Figur mit leichter Bewegung: Schweben, Augen-Glühen (Overlay), Blinzeln, je nach `zustand` Atmen und eine
 * einmalige Neigung. Nur `transform` und `opacity`, keine Gliedmaßen, kein 3D.
 *
 * R-D (Ausnahme, Betreiber 09.10.2026): die Figur OHNE KI-Funktion ist eine MARKE (Maskottchen) und erscheint und bewegt sich
 * immer; sie trägt weder `.ai-gated` noch `.ai-mark`. Modell-Funktionen (Antworten, Coach, freie Fragen) bleiben gated, das
 * entscheidet der Aufrufer um die Figur herum. Abweichung AW-018: Figur ohne KI-Funktion = Marke, nicht `.ai-gated`.
 *
 * Keyframes liegen als Animations-Tokens im Theme (`theme/hornaut-animation.css`, `animate-hornaut-*`), die Augen sind ein
 * SVG-Overlay in Bildkoordinaten (viewBox 376 x 960 = Seitenverhältnis der Figur), also ohne `style=` und ohne Arbitrary Values.
 * `motion-reduce`: alle Animationen aus, Augen statisch bei 70 % Deckkraft.
 *
 * Dekorativ: `alt=""` und `aria-hidden`.
 */

/** ruhig = Schweben + Augen-Puls + Blinzeln; spricht = zusätzlich Atmen und hellere, schnellere Augen; zeigt = einmalige Neigung. */
export type HornautFigurZustand = "ruhig" | "spricht" | "zeigt";

const FIGUR_1X = "/assets/hornaut/hornaut-ganzkoerper-480.webp";
const FIGUR_2X = "/assets/hornaut/hornaut-ganzkoerper-960.webp";
/** Breite zu Höhe der Figur-Datei (188 : 480 = 376 : 960). */
export const HORNAUT_FIGUR_BREITE_ZU_HOEHE = 188 / 480;

/** Augen-Overlay in Bildkoordinaten (viewBox 376 x 960): Mitte links 46,8 %, rechts 62,0 % der Breite, beide 9,7 % der Höhe. */
const AUGE_LINKS = { cx: 175.97, cy: 93.12 } as const;
const AUGE_RECHTS = { cx: 233.12, cy: 93.12 } as const;
/** Glühen 8 % x 5,4 %, Lid 5 % x 3,6 % der Bildfläche (Halbachsen). */
const GLUEHEN = { rx: 15.04, ry: 25.92 } as const;
const LID = { rx: 9.4, ry: 17.28 } as const;

export interface HornautFigurProps {
  /** Höhe in Pixel; die Breite folgt dem Seitenverhältnis. */
  hoehe: number;
  zustand?: HornautFigurZustand;
  /** Einblenden (16 px von unten, 400 ms) beim Einhängen. */
  erscheinen?: boolean;
  /** Zustand „zeigt“ ist zu Ende (Neigung fertig): der Aufrufer setzt dann auf „ruhig“. */
  onZustandEnde?: () => void;
  className?: string;
}

export function HornautFigur({ hoehe, zustand = "ruhig", erscheinen = false, onZustandEnde, className }: HornautFigurProps) {
  const verlauf = useId();
  const breite = Math.round(hoehe * HORNAUT_FIGUR_BREITE_ZU_HOEHE);
  const spricht = zustand === "spricht";
  const zeigt = zustand === "zeigt";

  const augen = spricht
    ? "text-primary-200 mix-blend-screen animate-hornaut-augen-spricht motion-reduce:animate-none opacity-75 motion-reduce:opacity-70"
    : "text-primary-200 mix-blend-screen animate-hornaut-augen-puls motion-reduce:animate-none opacity-60 motion-reduce:opacity-70";

  return (
    <span
      className={className ?? "inline-block shrink-0"}
      aria-hidden="true"
      data-hornaut-figur-baustein
      data-zustand={zustand}
    >
      {/* Erscheinen */}
      <span className={erscheinen ? "block animate-hornaut-erscheinen motion-reduce:animate-none" : "block"} data-hornaut-erscheinen={erscheinen ? "an" : "aus"}>
        {/* Schweben */}
        <span className="block animate-hornaut-schweben motion-reduce:animate-none">
          {/* Neigung (zeigt), Drehpunkt unten */}
          <span
            className={zeigt ? "block origin-bottom animate-hornaut-zeigen motion-reduce:animate-none" : "block origin-bottom"}
            data-hornaut-neigung={zeigt ? "an" : "aus"}
            onAnimationEnd={(e) => {
              if (zeigt && e.target === e.currentTarget) onZustandEnde?.();
            }}
          >
            {/* Atmen (spricht), Drehpunkt unten */}
            <span className={spricht ? "relative block origin-bottom animate-hornaut-atmen motion-reduce:animate-none" : "relative block origin-bottom"}>
              {/* eslint-disable-next-line @next/next/no-img-element -- statische Marken-Grafik, bewusst ohne Bildoptimierung (WebP mit Alpha liegt fertig vor) */}
              <img
                src={FIGUR_1X}
                srcSet={`${FIGUR_1X} 188w, ${FIGUR_2X} 376w`}
                sizes={`${breite}px`}
                width={breite}
                height={hoehe}
                alt=""
                aria-hidden="true"
                className="block shrink-0 object-contain"
                draggable={false}
              />
              {/* Augen-Overlay: Glühen (Hellfarbe, additiv) und Lid (kurzes Blinzeln) */}
              <svg
                className="absolute inset-0 size-full pointer-events-none"
                viewBox="0 0 376 960"
                preserveAspectRatio="none"
                focusable="false"
                aria-hidden="true"
                data-hornaut-augen
              >
                <defs>
                  <radialGradient id={verlauf}>
                    <stop offset="0" stopColor="currentColor" stopOpacity="0.95" />
                    <stop offset="0.55" stopColor="currentColor" stopOpacity="0.45" />
                    <stop offset="1" stopColor="currentColor" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <g className={augen} data-hornaut-gluehen>
                  <ellipse cx={AUGE_LINKS.cx} cy={AUGE_LINKS.cy} rx={GLUEHEN.rx} ry={GLUEHEN.ry} fill={`url(#${verlauf})`} />
                  <ellipse cx={AUGE_RECHTS.cx} cy={AUGE_RECHTS.cy} rx={GLUEHEN.rx} ry={GLUEHEN.ry} fill={`url(#${verlauf})`} />
                </g>
                <g className="fill-foreground opacity-0 animate-hornaut-blinzeln motion-reduce:animate-none" data-hornaut-lid>
                  <ellipse cx={AUGE_LINKS.cx} cy={AUGE_LINKS.cy} rx={LID.rx} ry={LID.ry} />
                  <ellipse cx={AUGE_RECHTS.cx} cy={AUGE_RECHTS.cy} rx={LID.rx} ry={LID.ry} />
                </g>
              </svg>
            </span>
          </span>
        </span>
      </span>
    </span>
  );
}
